import AuthCallbackPage from '@shared/components/loading/AuthCallbackPage';
import ToBeCreated from '@shared/components/loading/ToBeCreated';
import AdminHeaderOnlyLayout from 'admin/components/layout/AdminHeaderOnlyLayout';
import ProtectedLayout from 'admin/components/ProtectedLayout';
import { useEffect } from 'react';
import { createHashRouter, RouterProvider } from 'react-router';
import LoginPage from '../LoginPage';
import NotFoundPage from '../NotFoundPage';
import AccountSettingsPage from '../protected/AccountSettingsPage';
import DashboardPage from '../protected/DashboardPage';
import MetricLandingPage from '../protected/metric/MetricLandingPage';
import NotificationPage from '../protected/NotificationPage';
import LicenseUpgradePage from '../protected/org/LicenseUpgradePage';
import OrgBillingRedirectErrorPage from '../protected/org/OrgBillingRedirectErrorPage';
import OrgBillingRedirectPage from '../protected/org/OrgBillingRedirectPage';
import OrgDetailPage from '../protected/org/OrgDetailPage';
import OrgDocumentationPage from '../protected/org/OrgDocumentationPage';
import OrgInvitesPage from '../protected/org/OrgInvitesPage';
import OrgLicenseActivatedPage from '../protected/org/OrgLicenseActivatedPage';
import OrgLicenseKeyPage from '../protected/org/OrgLicenseKeyPage';
import OrgManagedOrgsPage from '../protected/org/OrgManagedOrgsPage';
import OrgMembersPage from '../protected/org/OrgMembersPage';
import OrgProductPage from '../protected/org/OrgProductPage';
import OrgSettingsPage from '../protected/org/OrgSettingsPage';
import ProjectClientDetailPage from '../protected/projects/ProjectClientDetailPage';
import ProjectDeviceTypeDetailPage from '../protected/projects/ProjectDeviceTypeDetailPage';
import ProjectsLandingPage from '../protected/projects/ProjectsLandingPage';
import ProjectsNewPage from '../protected/projects/ProjectsNewPage';
import DeveloperToolsPage from '../resources/DeveloperToolsPage';

const getBillingRoutes = () => [
    {
        path: 'upgrade',
        children: [
            {
                index: true,
                element: <OrgBillingRedirectPage />,
                handle: { crumbKey: 'common:license.redirect.title' },
            },
            {
                path: 'product/:productId',
                element: <OrgBillingRedirectPage />,
                handle: { crumbKey: 'common:license.redirect.title' },
            },
        ],
    },
    {
        path: 'error',
        element: <OrgBillingRedirectErrorPage />,
        handle: { crumbKey: 'common:license.redirect.error' },
    },
];

const getOrgRoutes = () => [
    {
        element: <OrgDetailPage />,
        index: true,
    },
    {
        path: 'billing/compare',
        handle: { crumbKey: 'common:billing.upgrade.compare' },
        element: <LicenseUpgradePage />,
    },
    {
        path: 'billing/redirect',
        children: getBillingRoutes(),
    },
    {
        path: 'trial/redirect',
        children: getBillingRoutes(),
    },
    {
        path: 'license',
        element: <OrgLicenseActivatedPage />,
        handle: { crumbKey: 'common:license.activate.title' },
    },
    {
        path: 'members',
        handle: { crumbKey: 'common:organization.member.titlePlural' },
        children: [
            {
                index: true,
                element: <OrgMembersPage />,
            },
            {
                path: 'invites',
                element: <OrgInvitesPage />,
                handle: { crumbKey: 'common:organization.invite.titlePlural' },
            },
        ],
    },
    {
        path: 'projects',
        handle: { crumbKey: 'common:organization.projects.title' },
        children: [
            {
                element: <ProjectsLandingPage />,
                index: true,
            },
            {
                path: 'new',
                element: <ProjectsNewPage />,
                handle: { crumbKey: 'common:organization.projects.create.title' },
            },
            {
                path: 'client/:clientId',
                element: <ProjectClientDetailPage />,
                handle: { crumbKey: 'common:organization.projects.clients.title' },
            },
            {
                path: 'device/:deviceId',
                element: <ProjectDeviceTypeDetailPage />,
                handle: { crumbKey: 'common:organization.projects.deviceType.title' },
            },
        ],
    },
    {
        path: 'metrics',
        handle: { crumbKey: 'common:organization.metrics.title' },
        children: [
            {
                element: <MetricLandingPage />,
                index: true,
            },
            {
                path: ':clientId',
                element: <ToBeCreated pageTitle="Org Metrics Client Detail" />,
                handle: { crumbKey: 'common:organization.project.title' },
            },
        ],
    },
    {
        path: 'documentation',
        handle: { crumbKey: 'common:resources.title' },
        children: [
            {
                element: <OrgDocumentationPage />,
                index: true,
            },
        ],
    },
    {
        path: 'settings',
        handle: { crumbKey: 'common:organization.settings.title' },
        children: [
            {
                element: <OrgSettingsPage />,
                index: true,
            },
        ],
    },
];

const router = createHashRouter([
    {
        path: '/',
        handle: { crumbKey: 'common:dashboard.title' },
        children: [
            {
                path: 'login',
                element: (
                    <AdminHeaderOnlyLayout>
                        <LoginPage />
                    </AdminHeaderOnlyLayout>
                ),
            },
            {
                path: 'authcallback',
                element: (
                    <AdminHeaderOnlyLayout>
                        <AuthCallbackPage />
                    </AdminHeaderOnlyLayout>
                ),
            },
            {
                element: <ProtectedLayout />,
                children: [
                    {
                        index: true,
                        element: <DashboardPage />,
                    },
                    {
                        path: 'upgrade',
                        handle: { crumbKey: 'common:upgrade' },
                        element: <LicenseUpgradePage />,
                    },
                    {
                        path: 'my-account',
                        element: <AccountSettingsPage />,
                        handle: { crumbKey: 'common:account.settings' },
                    },
                    {
                        path: 'notifications',
                        element: <NotificationPage />,
                        handle: { crumbKey: 'common:notifications.title' },
                    },

                    {
                        path: 'org/:organizationId',
                        handle: { crumbKey: 'common:organization.title' },
                        children: [
                            ...getOrgRoutes(),
                            {
                                path: 'managed',
                                element: <OrgManagedOrgsPage />,
                                handle: { crumbKey: 'common:organization.managed.title' },
                            },
                            {
                                path: 'managed/:managedOrgId',
                                handle: { crumbKey: 'common:organization.managed.title' },
                                children: getOrgRoutes(),
                            },
                            {
                                path: 'licensekey',
                                element: <OrgLicenseKeyPage />,
                                handle: { crumbKey: 'common:license.key.title' },
                            },
                            {
                                path: 'purchase',
                                element: <OrgProductPage />,
                                handle: { crumbKey: 'common:license.key.product.title' },
                            },
                            {
                                path: 'product/:productId',
                                element: <OrgBillingRedirectPage />,
                                handle: { crumbKey: 'common:license.redirect.title' },
                            },
                        ],
                    },

                    {
                        path: 'developer',
                        element: <DeveloperToolsPage />,
                        handle: { crumbKey: 'common:developer.title' },
                    },
                    {
                        path: 'support',
                        element: <ToBeCreated pageTitle="Support" />,
                        handle: { crumbKey: 'common:support.title' },
                    },
                ],
            },
            {
                path: '*',
                element: (
                    <AdminHeaderOnlyLayout>
                        <NotFoundPage />
                    </AdminHeaderOnlyLayout>
                ),
                handle: { crumbKey: 'common:notFound' },
            },
        ],
    },
]);

export default function Layout() {
    const restructureUrl = () => {
        const { search, hash } = window.location;
        // If there are query parameters but they are before the hash
        // specifically addresses the base url overwriting that happens with auth callbacks
        if (search && !hash.includes('?')) {
            let newHash = hash || '#/';
            const params = new URLSearchParams(search);

            // Directly move to authcallback if code is present to minimize the number of redirects
            if (search.includes('code')) {
                newHash = '#/authcallback';
            }

            const newUrl = `${window.location.origin}${newHash}${search}`;
            window.history.replaceState(null, '', newUrl);
            window.location.reload();
        }
    };

    useEffect(() => {
        restructureUrl();
    }, []);
    return <RouterProvider router={router} />;
}
